import { useEffect, useState, useCallback } from 'react';
import { X, AlertCircle, CheckCircle2, ChevronDown, ChevronRight, Sparkles } from 'lucide-react';
import { commissionReportsApi } from '../api/commissionReportsApi';

/**
 * Reviews the payees and offices a run's CRM names but that aren't yet in the
 * org's config, and lets the user create the ones they approve. Self-contained:
 * drop it into the month detail page and open it from a button once a CRM file
 * has been uploaded.
 *
 *   <PayeeDiscoveryReview
 *       orgUuid={orgUuid} year={year} month={month}
 *       open={showReview}
 *       onClose={() => setShowReview(false)}
 *       onConfirmed={(result) => { ...refresh config / show toast... }}
 *   />
 *
 * Flow: GET payee-suggestions → user toggles CREATE/SKIP per item →
 * POST payee-suggestions/confirm → show the created/skipped summary.
 *
 * Default actions by verdict:
 *   NEW              → CREATE (checked)
 *   EXISTING         → SKIP, locked (already in config, nothing to do)
 *   FUZZY_SUGGESTION → SKIP, but unlockable — shows the resembled name + score
 *                      so the user can flip to CREATE if it's genuinely distinct
 *
 * A SKIP office that still has CREATE payees under it is sent with its
 * existingUuid so those payees attach to the existing office — handled here so
 * the user never has to think about it.
 */
export default function PayeeDiscoveryReview({ orgUuid, year, month, open, onClose, onConfirmed }) {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [result, setResult] = useState(null);   // confirmation summary, once submitted

    // The working tree: { subby: Office[], referrer: Office[] }, each office and
    // payee augmented with a local `create` boolean and `expanded` state.
    const [tree, setTree] = useState(null);

    const load = useCallback(() => {
        if (!orgUuid) return;
        setLoading(true);
        setError(null);
        setResult(null);
        commissionReportsApi
            .discoverPayees(orgUuid, year, month)
            .then((data) => setTree(toWorkingTree(data)))
            .catch((err) => setError(extractMessage(err)))
            .finally(() => setLoading(false));
    }, [orgUuid, year, month]);

    useEffect(() => {
        if (open) load();
    }, [open, load]);

    if (!open) return null;

    const allOffices = tree ? [...tree.subby, ...tree.referrer] : [];
    const createCount =
        allOffices.filter((o) => o.create && o.verdict !== 'EXISTING').length +
        allOffices.reduce(
            (n, o) => n + o.payees.filter((p) => p.create && p.verdict !== 'EXISTING').length,
            0,
        );

    const toggleOffice = (side, idx) =>
        setTree((t) => mutate(t, side, idx, null, (o) => ({ ...o, create: !o.create })));

    const togglePayee = (side, oIdx, pIdx) =>
        setTree((t) => mutate(t, side, oIdx, pIdx, (p) => ({ ...p, create: !p.create })));

    const toggleExpand = (side, idx) =>
        setTree((t) => mutate(t, side, idx, null, (o) => ({ ...o, expanded: !o.expanded })));

    const submit = () => {
        setSubmitting(true);
        setError(null);
        const payload = { offices: toConfirmationPayload(allOffices) };
        commissionReportsApi
            .confirmPayees(orgUuid, year, month, payload)
            .then((res) => {
                // Normalise: the backend may omit empty lists from the JSON,
                // leaving these undefined. Default them so render is safe.
                const safe = {
                    createdOffices: res?.createdOffices ?? [],
                    createdPayees: res?.createdPayees ?? [],
                    skipped: res?.skipped ?? [],
                };
                setResult(safe);
                onConfirmed?.(safe);
            })
            .catch((err) => setError(extractMessage(err)))
            .finally(() => setSubmitting(false));
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            {/* Backdrop */}
            <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" onClick={onClose} />

            {/* Panel */}
            <div className="relative bg-white rounded-xl shadow-2xl border border-slate-200 w-full max-w-2xl max-h-[85vh] flex flex-col">
                {/* Header */}
                <div className="flex items-start justify-between px-6 py-4 border-b border-slate-200">
                    <div className="flex items-start gap-3">
                        <div className="mt-0.5 p-1.5 bg-brand-50 rounded-lg">
                            <Sparkles size={18} className="text-brand-600" />
                        </div>
                        <div>
                            <h3 className="font-display text-lg font-semibold text-slate-900">
                                Payees found in this file
                            </h3>
                            <p className="text-sm text-slate-500 mt-0.5">
                                Add the offices and payees from your CRM to your configuration.
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-1 -mr-1 text-slate-400 hover:text-slate-600 rounded-md hover:bg-slate-100"
                        aria-label="Close"
                    >
                        <X size={20} />
                    </button>
                </div>

                {/* Body */}
                <div className="flex-1 overflow-y-auto px-6 py-4">
                    {error && (
                        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md flex items-start gap-2 text-sm text-red-700">
                            <AlertCircle size={16} className="mt-0.5 flex-shrink-0" />
                            <span>{error}</span>
                        </div>
                    )}

                    {loading && <SkeletonRows />}

                    {!loading && result && <ConfirmationSummary result={result} />}

                    {!loading && !result && tree && (
                        <>
                            {allOffices.length === 0 && (
                                <EmptyState />
                            )}
                            <OfficeGroup
                                title="Distribution Partner entities"
                                side="subby"
                                offices={tree.subby}
                                onToggleOffice={toggleOffice}
                                onTogglePayee={togglePayee}
                                onToggleExpand={toggleExpand}
                            />
                            <OfficeGroup
                                title="Referrer entities"
                                side="referrer"
                                offices={tree.referrer}
                                onToggleOffice={toggleOffice}
                                onTogglePayee={togglePayee}
                                onToggleExpand={toggleExpand}
                            />
                        </>
                    )}
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between px-6 py-4 border-t border-slate-200 bg-slate-50 rounded-b-xl">
                    {result ? (
                        <>
                            <span className="text-sm text-slate-500">
                                {result.createdOffices.length + result.createdPayees.length} created,
                                {' '}{result.skipped.length} skipped
                            </span>
                            <button
                                onClick={onClose}
                                className="px-4 py-2 text-sm font-medium text-white bg-brand-600 rounded-md hover:bg-brand-700"
                            >
                                Done
                            </button>
                        </>
                    ) : (
                        <>
                            <span className="text-sm text-slate-500">
                                {createCount > 0
                                    ? `${createCount} to add`
                                    : 'Nothing selected to add'}
                            </span>
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={onClose}
                                    className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-800 rounded-md hover:bg-slate-100"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={submit}
                                    disabled={submitting || loading || createCount === 0}
                                    className="px-4 py-2 text-sm font-medium text-white bg-brand-600 rounded-md hover:bg-brand-700 disabled:opacity-40 disabled:cursor-not-allowed"
                                >
                                    {submitting ? 'Adding…' : `Add ${createCount || ''}`.trim()}
                                </button>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

// ── Sub-components ──────────────────────────────────────────────────────────

function OfficeGroup({ title, side, offices, onToggleOffice, onTogglePayee, onToggleExpand }) {
    if (!offices || offices.length === 0) return null;
    return (
        <div className="mb-5 last:mb-0">
            <h4 className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-2">
                {title}
            </h4>
            <div className="space-y-1.5">
                {offices.map((o, idx) => (
                    <OfficeRow
                        key={`${side}-${idx}`}
                        office={o}
                        onToggle={() => onToggleOffice(side, idx)}
                        onTogglePayee={(pIdx) => onTogglePayee(side, idx, pIdx)}
                        onToggleExpand={() => onToggleExpand(side, idx)}
                    />
                ))}
            </div>
        </div>
    );
}

function OfficeRow({ office, onToggle, onTogglePayee, onToggleExpand }) {
    const locked = office.verdict === 'EXISTING';
    const hasPayees = office.payees.length > 0;
    return (
        <div className="border border-slate-200 rounded-lg overflow-hidden">
            <div className="flex items-center gap-3 px-3 py-2.5 bg-white">
                <ActionCheckbox checked={office.create} disabled={locked} onChange={onToggle} />
                <button
                    onClick={hasPayees ? onToggleExpand : undefined}
                    className={`flex items-center gap-1.5 flex-1 min-w-0 text-left ${hasPayees ? 'cursor-pointer' : 'cursor-default'}`}
                >
                    {hasPayees && (
                        office.expanded
                            ? <ChevronDown size={15} className="text-slate-400 flex-shrink-0" />
                            : <ChevronRight size={15} className="text-slate-400 flex-shrink-0" />
                    )}
                    <span className="text-sm font-medium text-slate-800 truncate">{office.name}</span>
                    <VerdictBadge item={office} />
                </button>
                {hasPayees && (
                    <span className="text-xs text-slate-400 tabular-nums flex-shrink-0">
                        {office.payees.length} payee{office.payees.length === 1 ? '' : 's'}
                    </span>
                )}
            </div>

            {office.expanded && hasPayees && (
                <div className="border-t border-slate-100 bg-slate-50/60 divide-y divide-slate-100">
                    {office.payees.map((p, pIdx) => {
                        const pLocked = p.verdict === 'EXISTING';
                        return (
                            <div key={pIdx} className="flex items-center gap-3 pl-9 pr-3 py-2">
                                <ActionCheckbox checked={p.create} disabled={pLocked} onChange={() => onTogglePayee(pIdx)} />
                                <span className="text-sm text-slate-700 truncate flex-1">{p.name}</span>
                                <VerdictBadge item={p} />
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

function ActionCheckbox({ checked, disabled, onChange }) {
    return (
        <input
            type="checkbox"
            checked={checked}
            disabled={disabled}
            onChange={onChange}
            className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500 disabled:opacity-40 cursor-pointer disabled:cursor-not-allowed flex-shrink-0"
        />
    );
}

function VerdictBadge({ item }) {
    if (item.verdict === 'EXISTING') {
        return (
            <span className="inline-flex items-center px-1.5 py-0.5 text-[11px] font-medium rounded text-slate-500 bg-slate-100 flex-shrink-0">
                In config
            </span>
        );
    }
    if (item.verdict === 'FUZZY_SUGGESTION') {
        const pct = item.fuzzyScore != null ? Math.round(item.fuzzyScore * 100) : null;
        return (
            <span
                className="inline-flex items-center px-1.5 py-0.5 text-[11px] font-medium rounded text-amber-700 bg-amber-50 border border-amber-200 flex-shrink-0"
                title={item.fuzzyCandidate ? `Resembles “${item.fuzzyCandidate}”${pct != null ? ` (${pct}%)` : ''}` : undefined}
            >
                Similar to “{truncate(item.fuzzyCandidate, 24)}”{pct != null ? ` · ${pct}%` : ''}
            </span>
        );
    }
    return (
        <span className="inline-flex items-center px-1.5 py-0.5 text-[11px] font-medium rounded text-emerald-700 bg-emerald-50 border border-emerald-200 flex-shrink-0">
            New
        </span>
    );
}

function ConfirmationSummary({ result }) {
    const created = [...result.createdOffices, ...result.createdPayees];
    return (
        <div>
            {created.length > 0 && (
                <div className="mb-4 p-3 bg-emerald-50 border border-emerald-200 rounded-md flex items-start gap-2">
                    <CheckCircle2 size={16} className="mt-0.5 flex-shrink-0 text-emerald-600" />
                    <div className="text-sm text-emerald-800">
                        <p className="font-medium">
                            Added {result.createdOffices.length} office{result.createdOffices.length === 1 ? '' : 's'} and
                            {' '}{result.createdPayees.length} payee{result.createdPayees.length === 1 ? '' : 's'}.
                        </p>
                    </div>
                </div>
            )}
            {result.skipped.length > 0 && (
                <div>
                    <h4 className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-2">
                        Skipped
                    </h4>
                    <div className="space-y-1">
                        {result.skipped.map((s, i) => (
                            <div key={i} className="flex items-baseline gap-2 text-sm">
                                <span className="text-[11px] font-medium text-slate-400 uppercase w-12 flex-shrink-0">{s.kind}</span>
                                <span className="text-slate-700">{s.name}</span>
                                <span className="text-slate-400">— {s.reason}</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

function EmptyState() {
    return (
        <div className="py-10 text-center">
            <p className="text-sm text-slate-500">
                No new payees found — everything in this file is already in your configuration.
            </p>
        </div>
    );
}

function SkeletonRows() {
    return (
        <div className="space-y-2">
            {[0, 1, 2].map((i) => (
                <div key={i} className="h-11 bg-slate-100 rounded-lg animate-pulse" />
            ))}
        </div>
    );
}

// ── Tree <-> payload helpers ────────────────────────────────────────────────

/** Backend result → working tree with local UI state (create / expanded). */
function toWorkingTree(data) {
    const mapOffice = (o) => ({
        ...o,
        create: o.verdict === 'NEW',           // NEW defaults on; EXISTING/FUZZY default off
        expanded: hasCreatablePayee(o) || o.verdict === 'NEW',
        payees: (o.payees ?? []).map((p) => ({
            ...p,
            create: p.verdict === 'NEW',
        })),
    });
    return {
        subby: (data.subbyOffices ?? []).map(mapOffice),
        referrer: (data.referrerOffices ?? []).map(mapOffice),
    };
}

function hasCreatablePayee(office) {
    return (office.payees ?? []).some((p) => p.verdict === 'NEW');
}

/**
 * Working offices → confirmation request payload. An office is CREATE when its
 * box is checked AND it isn't already in config; otherwise SKIP. A SKIP office
 * still carries existingUuid (when known) so its CREATE payees can attach.
 */
function toConfirmationPayload(offices) {
    return offices.map((o) => {
        const create = o.create && o.verdict !== 'EXISTING';
        return {
            action: create ? 'CREATE' : 'SKIP',
            existingUuid: o.existingUuid ?? null,
            name: o.name,
            type: o.payees[0]?.type ?? null,   // office side, for new-payee defaulting
            abn: null,
            contactName: null,
            contactEmail: null,
            contactPhone: null,
            address: null,
            payees: o.payees.map((p) => ({
                action: (p.create && p.verdict !== 'EXISTING') ? 'CREATE' : 'SKIP',
                type: p.type,
                name: p.name,
                code: null,
                contactName: null,
                contactEmail: null,
                contactPhone: null,
            })),
        };
    });
}

/** Immutable update of one office (pIdx null) or one payee within it. */
function mutate(tree, side, oIdx, pIdx, fn) {
    const offices = tree[side].map((o, i) => {
        if (i !== oIdx) return o;
        if (pIdx == null) return fn(o);
        return { ...o, payees: o.payees.map((p, j) => (j === pIdx ? fn(p) : p)) };
    });
    return { ...tree, [side]: offices };
}

function extractMessage(err) {
    return err?.response?.data?.message || err?.message || 'Something went wrong';
}

function truncate(s, n) {
    if (!s) return '';
    return s.length > n ? s.slice(0, n - 1) + '…' : s;
}